import { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import BigButton from '../components/BigButton'
import FeedbackForm from '../components/FeedbackForm'
import MapPicker from '../components/MapPicker'
import PaymentBlock from '../components/PaymentBlock'
import { api } from '../lib/api'
import { nlDagMaand, nlDatum } from '../lib/format'
import { reopenSentence } from '../lib/season'
import { spotDisplayNameById, spotIsGroundMarkedById } from '../lib/spotLabel'
import type {
  ArchivedEdition, MapMeta, MyFeedback, MyRegistration, Payment, PublicSpot, Season,
} from '../lib/types'

type LoadResp = {
  // null zodra de inschrijving niet meer in Notion staat maar de token nog uit
  // een afgesloten editie gekend is — de persoonlijke link blijft dan werken.
  registration: MyRegistration | null
  payment: Payment | null
  feedback: MyFeedback | null
  history: ArchivedEdition[]
  season: Season
  meta: MapMeta
  spots: PublicSpot[]
  openSeating: boolean
}

export default function MijnPlek() {
  const { token = '' } = useParams()
  // `v` proves this page was reached via the mailed link, not the token the
  // register-response hands straight back — see signToken() in server/util.ts.
  const [searchParams] = useSearchParams()
  const verify = searchParams.get('v') ?? ''
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<LoadResp | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState<string[]>([])
  const [changing, setChanging] = useState(false)
  const [busy, setBusy] = useState(false)
  const [pickError, setPickError] = useState<string | null>(null)

  async function load() {
    setLoading(true)
    setError(null)
    const qs = verify ? `?v=${encodeURIComponent(verify)}` : ''
    const res = await api<LoadResp>(`/api/me/${encodeURIComponent(token)}${qs}`)
    setLoading(false)
    if (!res.ok) {
      setError(res.error === 'not-found' ? 'not-found' : 'server')
      return
    }
    setData(res.data)
    setPending([])
    setChanging(false)
    setPickError(null)
  }

  useEffect(() => {
    if (!token) return
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  if (loading) return <div className="container-narrow py-12 text-muted">Laden…</div>
  if (error === 'not-found') {
    return (
      <div className="container-narrow py-12">
        <div className="card p-8 shadow-block text-center">
          <h1 className="font-display text-5xl tracking-display uppercase mb-3">
            Link niet gevonden
          </h1>
          <p className="text-base mb-2">
            We kennen deze link niet. Mogelijk is hij ergens afgekapt
            in je e-mail, of werd je inschrijving verwijderd.
          </p>
          <p className="text-base text-muted mb-6">
            Schrijf je opnieuw in of stuur ons een mail —
            we zoeken het samen uit.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:justify-center sm:items-center">
            <Link to="/inschrijven">
              <BigButton>Opnieuw inschrijven</BigButton>
            </Link>
            <a
              href="mailto:rommelmarkt@fluweelfeest.be"
              className="text-sm text-muted underline self-center"
            >
              rommelmarkt@fluweelfeest.be
            </a>
          </div>
        </div>
      </div>
    )
  }
  if (error || !data) {
    return (
      <div className="container-narrow py-12">
        <h1 className="font-display text-2xl mb-2">Er ging iets mis</h1>
        <p className="text-muted">Probeer de pagina opnieuw te laden.</p>
      </div>
    )
  }

  const history = data.history ?? []
  const season = data.season
  const seizoenGesloten = season ? !season.registrationOpen : false

  // Geen lopende inschrijving meer, wel een bewaarde editie: toon het archief.
  if (!data.registration) {
    return <ArchiefOnly token={token} history={history} season={season} />
  }

  const me = data.registration
  const wanted = me.aantalPlaatsen
  const claimed = me.spots.length
  const compleet = claimed === wanted
  const partial = claimed > 0 && claimed < wanted
  const leeg = claimed === 0
  // Self-picking / re-picking is only possible once the organiser opens phase 2
  // ("open seating"). While it's closed the market is assigned by the organiser,
  // so nobody sees a picker — not even to swap an already-assigned spot.
  const mayChangeFully = data.openSeating
  const myVoornaam = me.naam.split(/\s+/)[0]
  // Does one of my assigned spots sit in a car-free zone (Fluweelpark)?
  const myCarFree = me.spots.some((id) => data.spots.find((p) => p.id === id)?.carFree)
  // Does one of my spots lack a real facade/address (park, parking, corner…)?
  const myGroundMarked = me.spots.some((id) => spotIsGroundMarkedById(id, data.spots))

  // Na de markt: geen plek meer te kiezen of te betalen. De pagina wordt de
  // bedankt-en-feedbackpagina, met de links om ze later terug te vinden.
  if (seizoenGesloten) {
    // Prefer the label frozen at closing time over resolving live against
    // data.spots — spots.json can be redrawn or reimported for the next
    // edition before the reopen date, which would otherwise show this
    // finished edition's spot under the next one's address (or degrade a
    // removed id to its bare code). The server already resolves this the
    // same way (frozen, falling back to live) in the same order as
    // `me.spots` — see resolveSpotLabels() in server/archive.ts. This row is
    // never in `history` itself (only *other* past editions are), so it has
    // to come off the live `registration`, not a history lookup.
    // Lengte-check, geen `??`: de server stuurt dit veld altijd mee, desnoods
    // als lege array (bevriezen kan mislukt zijn, of de optionele Notion-kolom
    // ontbreekt). Met `??` sloeg de terugval dus nooit aan en verdween de plek
    // uit deze zin terwijl ze gewoon bekend was.
    const mijnPlekken = me.plekAdresLabels && me.plekAdresLabels.length > 0
      ? me.plekAdresLabels
      : me.spots.map((id) => spotDisplayNameById(id, data.spots))
    return (
      <Layout editie={season?.activeEdition} name={myVoornaam} token={token} history={history} season={season} verify={verify}>
        <div className="border-[3px] border-ink bg-lime shadow-block p-4 mb-4">
          <p className="font-display text-3xl tracking-display uppercase">
            De rommelmarkt zit erop
          </p>
          <p className="text-base mt-1">
            {/* Tijdens een gesloten seizoen IS de actieve editie de zonet
                gesloten editie (zie activeEditionYear() in server/state.ts),
                dus season.eventDate is hier de markt waar deze standhouder
                net op stond. Zonder datum valt het terug op het jaartal, en
                zonder dat op een zin zonder tijdsaanduiding. */}
            Bedankt dat je erbij was{
              season?.eventDateLabel
                ? ` op ${season.eventDateLabel}`
                : season?.editionYear ? ` in ${season.editionYear}` : ''
            }!
          </p>
        </div>
        <p className="text-lg">
          {mijnPlekken.length > 0
            ? <>Je stond op <strong>{mijnPlekken.join(', ')}</strong>.</>
            : me.wachtlijst
              ? <>Je stond deze editie op de <strong>wachtlijst</strong>.</>
              : <>Je was ingeschreven voor <strong>{wanted}</strong> {wanted === 1 ? 'plek' : 'plekken'}.</>}
        </p>
        <p className="text-base text-muted">
          Eén vraagje nog: hoe was het? Je antwoord bepaalt mee hoe we de volgende editie aanpakken.
        </p>
        <div className="mt-4">
          <FeedbackForm token={token} initial={data.feedback} />
        </div>
      </Layout>
    )
  }

  // Waitlist: market is full. No spot pick, no payment — just a clear notice.
  if (me.wachtlijst && me.spots.length === 0) {
    return (
      <Layout editie={season?.activeEdition} name={myVoornaam} token={token} history={history} season={season} verify={verify}>
        <div className="border-[3px] border-ink bg-lime shadow-block p-4 mb-4 flex items-center gap-3">
          <span className="font-display text-3xl tracking-display uppercase">Op de wachtlijst</span>
        </div>
        <p className="text-lg">
          Bedankt voor je inschrijving! De rommelmarkt zit op dit moment <strong>vol</strong>, dus je staat
          op de <strong>wachtlijst</strong>.
        </p>
        <p>Komt er een plek vrij, dan contacteren we je via mail. Je hoeft nu <strong>nog niets te betalen</strong>.</p>
        <div className="card p-3 shadow-block mt-4">
          <MapPicker spots={data.spots} meta={data.meta} mode="view" />
          <Legend />
        </div>
      </Layout>
    )
  }

  if (leeg && me.buurtbewoner && !data.openSeating) {
    return (
      <Layout editie={season?.activeEdition} name={myVoornaam} payment={data.payment} betaald={me.betaald} token={token} history={history} season={season} verify={verify}>
        <p className="text-lg">We hebben je inschrijving ontvangen.</p>
        <p>Het team zoekt jouw plek manueel — je hoort hier deze week iets over.</p>
      </Layout>
    )
  }

  if (leeg && !me.buurtbewoner && !data.openSeating) {
    return (
      <Layout editie={season?.activeEdition} name={myVoornaam} payment={data.payment} betaald={me.betaald} token={token} history={history} season={season} verify={verify}>
        <p className="text-lg">
          We hebben je inschrijving ontvangen. <strong>Wij wijzen je een plek toe</strong> en laten
          het je per mail weten.
        </p>
        <p>Ondertussen kan je al een blik werpen op de plattegrond — grijs is bezet, geel is nog vrij.</p>
        <div className="card p-3 shadow-block mt-4">
          <MapPicker spots={data.spots} meta={data.meta} mode="view" />
          <Legend />
        </div>
      </Layout>
    )
  }

  // Bevestigd state — only when the user hasn't requested a change.
  // Clicking "Een andere plek kiezen" sets `changing`, which drops us into
  // the Kies-modus block below; "Annuleer" returns here.
  if (compleet && !changing) {
    return (
      <Layout editie={season?.activeEdition} name={myVoornaam} payment={data.payment} betaald={me.betaald} token={token} history={history} season={season} verify={verify}>
        <p className="text-lg">
          Je plek{me.spots.length > 1 ? 'ken' : ''}:{' '}
          <strong>{me.spots.map((id) => spotDisplayNameById(id, data.spots)).join(', ')}</strong>.
        </p>
        {myGroundMarked && (
          <p className="text-sm text-muted mt-1">
            📍 Je plek is ter plaatse op de grond aangeduid.
          </p>
        )}
        {myCarFree && <ParkNotice />}
        <div className="card p-3 shadow-block mt-4">
          <MapPicker
            spots={data.spots}
            meta={data.meta}
            mode="view"
            mySpotIds={me.spots}
          />
          <Legend showPark={myCarFree} />
        </div>
        {mayChangeFully && (
          <button
            type="button"
            onClick={() => { setChanging(true); setPending(me.spots); setPickError(null) }}
            className="text-sm text-muted underline mt-3 hover:text-ink"
          >
            Een andere plek kiezen
          </button>
        )}
      </Layout>
    )
  }

  // Self-picking closed: a partially-assigned registrant sees their spots
  // read-only, with a note that the rest is assigned by the organiser.
  if (partial && !data.openSeating) {
    return (
      <Layout editie={season?.activeEdition} name={myVoornaam} payment={data.payment} betaald={me.betaald} token={token} history={history} season={season} verify={verify}>
        <p className="text-lg">
          Je hebt al <strong>{claimed}</strong> van je <strong>{wanted}</strong> plek{wanted > 1 ? 'ken' : ''}:{' '}
          <strong>{me.spots.join(', ')}</strong>. De overige {wanted - claimed} wijzen we je toe —
          je hoort het per mail.
        </p>
        {myCarFree && <ParkNotice />}
        <div className="card p-3 shadow-block mt-4">
          <MapPicker spots={data.spots} meta={data.meta} mode="view" mySpotIds={me.spots} />
          <Legend showPark={myCarFree} />
        </div>
      </Layout>
    )
  }

  // Kies-modus: partial OR (leeg + (buurtbewoner-met-tekort OR open seating)).
  // For partial: ask only for the missing count.
  const max = wanted
  // start kies-modus: pre-fill with already-claimed (so partial shows them as "selected" too).
  const initialPick = partial ? me.spots : pending
  const selected = pending.length > 0 ? pending : initialPick
  const remaining = max - selected.length
  const carFreeAvailable = data.spots.some((s) => s.carFree && !s.taken)
  const selectedHasCarFree = selected.some((id) => data.spots.find((p) => p.id === id)?.carFree)

  function toggle(id: string) {
    if (selected.includes(id)) {
      setPending(selected.filter((x) => x !== id))
    } else if (selected.length < max) {
      setPending([...selected, id])
    }
  }

  // Recommended = neighbours of currently-selected polygons. Computed naively
  // here without the server graph — cheap O(N²) over polygon vertices.
  // NOTE: plain computation, not useMemo — this code runs only after several
  // conditional early returns above, so a hook here would break the Rules of
  // Hooks (the hook count would differ between the loading render and this one)
  // and crash with React error #310.
  const recommendedIds = computeRecommendedIds(selected, data.spots)

  async function confirmPick() {
    setBusy(true)
    setPickError(null)
    const res = await api<{ registration: MyRegistration }>(
      `/api/me/${encodeURIComponent(token)}/spot`,
      { method: 'POST', body: JSON.stringify({ spotIds: selected }) },
    )
    setBusy(false)
    if (!res.ok) {
      if (res.error === 'taken') {
        setPickError('Eén van de gekozen plekken is net door iemand anders gepakt. Kies opnieuw.')
      } else if (res.error === 'phase-locked') {
        setPickError('Plek-keuze is momenteel niet mogelijk.')
      } else if (res.error === 'season-closed') {
        // Shouldn't normally be reachable — the season-closed branch above
        // hides this picker entirely — but a tab left open from before
        // closing could still reach this. `load()` below re-fetches and
        // re-renders into that branch.
        setPickError('De rommelmarkt zit erop — plekken kiezen kan niet meer.')
      } else {
        setPickError('Er ging iets mis. Probeer het opnieuw.')
      }
      load()
      return
    }
    await load()
  }

  function cancelChange() {
    setChanging(false)
    setPending([])
    setPickError(null)
  }

  return (
    <Layout editie={season?.activeEdition} name={myVoornaam} payment={data.payment} betaald={me.betaald} token={token} history={history} season={season} verify={verify}>
      <p className="text-lg">
        Kies <strong>{wanted}</strong> {wanted === 1 ? 'plek' : 'plekken'} —
        <strong> {selected.length} / {wanted}</strong> geselecteerd
        {remaining > 0 ? ` (nog ${remaining} te kiezen)` : ''}.
      </p>
      {carFreeAvailable && (
        <div className="mt-4 border-[3px] border-ink bg-[#7fb3ff]/25 px-4 py-3 text-base">
          🚲 <strong>Blauwe plekken liggen in het Fluweelpark.</strong> Daar kan je <strong>niet met de auto</strong> komen —
          ideaal als je te voet of met de (bak)fiets komt.
        </div>
      )}
      <div className="card p-3 shadow-block mt-4">
        <MapPicker
          spots={data.spots}
          meta={data.meta}
          mode="multi"
          mySpotIds={partial ? me.spots : []}
          selectedIds={selected}
          recommendedIds={recommendedIds}
          onToggle={toggle}
        />
        <Legend showRecommended showPark={carFreeAvailable} />
      </div>
      {selectedHasCarFree && (
        <p className="mt-4 border-[3px] border-ink bg-[#7fb3ff]/30 px-4 py-3 text-sm">
          ⚠️ Je koos een plek in het <strong>Fluweelpark</strong> — hier geraak je <strong>niet met de auto</strong>.
          Kom je toch met de wagen? Kies dan een plek in een straat.
        </p>
      )}
      {pickError && (
        <p
          role="alert"
          className="mt-4 border-[3px] border-ink bg-red text-offwhite px-4 py-3 font-display tracking-display uppercase text-sm"
        >
          {pickError}
        </p>
      )}
      <div className="mt-4 flex flex-col sm:flex-row gap-3 sm:items-center">
        <BigButton onClick={confirmPick} disabled={busy || selected.length !== wanted}>
          {busy ? 'Bezig…' : 'Bevestig mijn plek' + (wanted > 1 ? 'ken' : '')}
        </BigButton>
        {changing && compleet && (
          <button
            type="button"
            onClick={cancelChange}
            className="text-sm text-muted underline hover:text-ink"
          >
            Annuleer en hou m'n huidige plek
          </button>
        )}
      </div>
    </Layout>
  )
}

function Layout({
  name,
  payment,
  betaald,
  token,
  history = [],
  season,
  verify = '',
  editie = null,
  children,
}: {
  name: string
  payment?: Payment | null
  betaald?: boolean
  token?: string
  history?: ArchivedEdition[]
  season?: Season
  verify?: string
  // Het editiejaar boven de titel. Dezelfde persoonlijke link blijft jaren
  // werken en toont dan telkens een ándere editie (en buiten het seizoen de
  // editie die net gedaan is), dus zonder dit jaartal is er op deze pagina
  // niets dat zegt over wélke markt ze gaat.
  editie?: number | null
  children: React.ReactNode
}) {
  // Buiten het seizoen slaan we de "praktisch op de dag zelf"-balk over — die
  // gaat over een markt die al geweest is.
  const gesloten = season ? !season.registrationOpen : false
  // "12 september" van de lopende editie, of leeg zolang de organisator hem
  // niet ingevuld heeft — dan zegt de zin gewoon "tot op de markt".
  const dagMaand = nlDagMaand(season?.eventDate)
  return (
    <div className="mx-auto max-w-3xl px-5 py-8 space-y-5">
      {editie != null && (
        <p className="font-display text-xs sm:text-sm tracking-display uppercase text-muted -mb-3">
          Rommelmarkt {editie}
        </p>
      )}
      <h1 className="font-display text-6xl tracking-display uppercase mb-1">Hallo {name}!</h1>
      {betaald ? (
        <div className="border-[3px] border-ink bg-lime shadow-block p-4 flex items-center gap-3">
          <span className="font-display text-3xl tracking-display uppercase">Betaald ✓</span>
          <span className="text-base">
            Je plek is definitief bevestigd.{dagMaand ? ` Tot ${dagMaand}!` : ' Tot op de markt!'}
          </span>
        </div>
      ) : payment ? (
        <PaymentBlock payment={payment} title="Wachten op betaling" variant="ink" />
      ) : null}
      <div className="text-lg">{children}</div>
      {!gesloten && (
        <div className="border-[3px] border-ink bg-offwhite p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <p className="text-base">
            <strong>Praktisch op de dag zelf</strong> — uren, auto's, opruimen, betaling.
          </p>
          <Link to="/praktisch" className="font-display text-2xl tracking-display uppercase underline shrink-0">
            Lees de info
          </Link>
        </div>
      )}
      {token && <ProfielBlok token={token} history={history} season={season} verify={verify} />}
    </div>
  )
}

// Vaste voet van de persoonlijke pagina: het archieflinkje naar vorige edities
// en, buiten het seizoen, wanneer inschrijven weer kan. Zo blijft dezelfde link
// jaar na jaar bruikbaar.
function ProfielBlok({
  token,
  history,
  season,
  verify,
}: {
  token: string
  history: ArchivedEdition[]
  season?: Season
  verify: string
}) {
  const gesloten = season ? !season.registrationOpen : false
  if (history.length === 0 && !gesloten) return null
  return (
    <div className="border-[3px] border-ink bg-offwhite p-4 space-y-3">
      {history.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 justify-between">
          <p className="text-base">
            <strong>Je inschrijving van {history.map((h) => h.year).join(', ')}</strong> — je plek,
            je aantal standjes en wat je betaalde.
          </p>
          <Link
            to={`/mijn-plek/${encodeURIComponent(token)}/vorige-editie${verify ? `?v=${encodeURIComponent(verify)}` : ''}`}
            className="font-display text-2xl tracking-display uppercase underline shrink-0"
          >
            Bekijk
          </Link>
        </div>
      )}
      {gesloten && (
        <p className="text-base text-muted">
          Inschrijven voor de volgende editie kan weer <strong>{reopenSentence(season ?? null)}</strong>.
          Bewaar deze link — je komt er dan opnieuw op terecht.
        </p>
      )}
    </div>
  )
}

// De inschrijving staat niet meer in Notion (nieuwe editie, oude rijen weg),
// maar de token is nog gekend uit een afgesloten editie. De link blijft dus
// werken en toont wat we bewaard hebben.
function ArchiefOnly({
  token,
  history,
  season,
}: {
  token: string
  history: ArchivedEdition[]
  season?: Season
}) {
  const laatste = history[0]
  const voornaam = (laatste?.naam ?? '').split(/\s+/)[0] || 'rommelmarkt-vriend'
  const open = season ? season.registrationOpen : true
  return (
    <Layout name={voornaam} token={token} history={history} season={season}>
      <p className="text-lg">
        Je hebt op dit moment geen lopende inschrijving, maar we kennen je nog van{' '}
        <strong>{laatste ? laatste.year : 'een vorige editie'}</strong>
        {/* De datum van díé editie, van de archiefrij zelf. `season.eventDate`
            zou hier de marktdag van de nú lopende editie zijn — precies het
            jaartal dat deze zin níét bedoelt. */}
        {laatste?.eventDate ? <> — {nlDatum(laatste.eventDate)}</> : null}.
      </p>
      {/* Buiten het seizoen zegt het profielblok hieronder al wanneer inschrijven
          weer kan, dus tonen we hier enkel de knop als het effectief open is. */}
      {open && (
        <div className="mt-4">
          <Link to="/inschrijven">
            <BigButton>Schrijf je in voor deze editie</BigButton>
          </Link>
        </div>
      )}
    </Layout>
  )
}

function ParkNotice() {
  return (
    <div className="mt-4 border-[3px] border-ink bg-[#7fb3ff]/25 px-4 py-3 text-base">
      🚲 Jouw plek ligt in het <strong>Fluweelpark</strong> — daar kan je <strong>niet met de auto</strong> komen.
      Kom te voet of met de (bak)fiets.
    </div>
  )
}

function Legend({ showRecommended = false, showPark = false }: { showRecommended?: boolean; showPark?: boolean } = {}) {
  const swatch = (color: string) => (
    <span className="inline-block w-4 h-4 mr-1.5 align-middle border-[2px] border-ink" style={{ background: color }} />
  )
  return (
    <div className="flex flex-wrap gap-4 mt-3 text-sm font-display tracking-display uppercase">
      <span>{swatch('#e5ff73')}vrij</span>
      {showPark && <span>{swatch('#7fb3ff')}park · geen auto</span>}
      <span>{swatch('#5a5a5a')}bezet</span>
      <span>{swatch('#A077F3')}jouw plek</span>
      <span>{swatch('#cf2e2e')}geselecteerd</span>
      {showRecommended && <span>{swatch('#ff8800')}aanbevolen (naast je vorige keuze)</span>}
    </div>
  )
}

function computeRecommendedIds(selected: string[], spots: PublicSpot[]): string[] {
  if (selected.length === 0) return []
  const rec = new Set<string>()
  for (const sel of selected) {
    const polygon = spots.find((p) => p.id === sel)
    if (!polygon) continue
    for (const other of spots) {
      if (other.id === sel || selected.includes(other.id) || other.taken) continue
      if (sharesAnyEdge(polygon.coordinates, other.coordinates)) rec.add(other.id)
    }
  }
  return [...rec]
}

const TOL = 1e-5
function approx(a: [number, number], b: [number, number]): boolean {
  return Math.abs(a[0] - b[0]) < TOL && Math.abs(a[1] - b[1]) < TOL
}
function sharesAnyEdge(a: [number, number][], b: [number, number][]): boolean {
  for (let i = 0; i < a.length - 1; i++) {
    for (let j = 0; j < b.length - 1; j++) {
      if (
        (approx(a[i], b[j]) && approx(a[i + 1], b[j + 1])) ||
        (approx(a[i], b[j + 1]) && approx(a[i + 1], b[j]))
      ) return true
    }
  }
  return false
}
